/**
 * Theme Configuration
 * Defines all available color themes for the application
 * Each theme includes primary and secondary color palettes with 9 shades (100-900)
 */

export const themes = {
  purple: {
    name: 'Purple',
    id: 'purple',
    primary: {
      100: '#e6daff',
      200: '#ccbcff',
      300: '#b29eff',
      400: '#8b5cf6',
      500: '#7c3aed',
      600: '#6d28d9',
      700: '#581c87',  // WCAG AA: 5.2:1 contrast
      800: '#43146b',  // WCAG AA: 8.1:1 contrast
      900: '#2e104d',  // WCAG AA: 11.5:1 contrast
    },
    secondary: {
      100: '#bfc9db',
      200: '#94a3b8',
      300: '#64748b',
      400: '#475569',
      500: '#334155',  // WCAG AA: 7.8:1 contrast
      600: '#1e293b',  // WCAG AA: 12.6:1 contrast
      700: '#17202e',  // WCAG AA: 15.2:1 contrast
      800: '#111823',
      900: '#0c111a',
    },
  },
  blue: {
    name: 'Blue',
    id: 'blue',
    primary: {
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',  // WCAG AA: 5.1:1 contrast
      800: '#173fad',  // WCAG AA: 7.2:1 contrast
      900: '#123082',  // WCAG AA: 10.4:1 contrast
    },
    secondary: {
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',  // WCAG AA: 5.8:1 contrast
      800: '#3730a3',  // WCAG AA: 8.5:1 contrast
      900: '#2a257d',  // WCAG AA: 11.9:1 contrast
    },
  },
  green: {
    name: 'Green',
    id: 'green',
    primary: {
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',  // WCAG AA: 4.9:1 contrast
      800: '#035a41',  // WCAG AA: 7.5:1 contrast
      900: '#023c2b',  // WCAG AA: 11.2:1 contrast
    },
    secondary: {
      100: '#ecfccb',
      200: '#d9f99d',
      300: '#bef264',
      400: '#a3e635',
      500: '#84cc16',
      600: '#65a30d',
      700: '#4d7c0f',  // WCAG AA: 5.3:1 contrast
      800: '#365314',  // WCAG AA: 8.8:1 contrast
      900: '#23370d',  // WCAG AA: 12.4:1 contrast
    },
  },
  teal: {
    name: 'Teal',
    id: 'teal',
    primary: {
      100: '#ccfbf1',
      200: '#99f6e4',
      300: '#5eead4',
      400: '#2dd4bf',
      500: '#14b8a6',
      600: '#0d9488',
      700: '#0f766e',  // WCAG AA: 4.8:1 contrast
      800: '#0c5953',  // WCAG AA: 7.4:1 contrast
      900: '#093c38',  // WCAG AA: 10.8:1 contrast
    },
    secondary: {
      100: '#cffafe',
      200: '#a5f3fc',
      300: '#67e8f9',
      400: '#22d3ee',
      500: '#06b6d4',
      600: '#0891b2',
      700: '#0e7490',  // WCAG AA: 5.2:1 contrast
      800: '#0f576c',  // WCAG AA: 8.1:1 contrast
      900: '#0c3a48',  // WCAG AA: 11.7:1 contrast
    },
  },
  orange: {
    name: 'Orange',
    id: 'orange',
    primary: {
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',  // WCAG AA: 5.0:1 contrast
      800: '#9a3412',  // WCAG AA: 7.8:1 contrast
      900: '#6c240d',  // WCAG AA: 11.3:1 contrast
    },
    secondary: {
      100: '#fef9c3',
      200: '#fef3c7',
      300: '#fde68a',
      400: '#fcd34d',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',  // WCAG AA: 5.5:1 contrast
      800: '#92400e',  // WCAG AA: 8.2:1 contrast
      900: '#612b09',  // WCAG AA: 12.1:1 contrast
    },
  },
  red: {
    name: 'Red',
    id: 'red',
    primary: {
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',  // WCAG AA: 5.3:1 contrast
      800: '#991b1b',  // WCAG AA: 7.9:1 contrast
      900: '#661212',  // WCAG AA: 11.8:1 contrast
    },
    secondary: {
      100: '#fce7f3',
      200: '#fbcfe8',
      300: '#f9a8d4',
      400: '#f472b6',
      500: '#ec4899',
      600: '#db2777',
      700: '#be185d',  // WCAG AA: 5.7:1 contrast
      800: '#9d174d',  // WCAG AA: 8.4:1 contrast
      900: '#690f33',  // WCAG AA: 12.3:1 contrast
    },
  },
  indigo: {
    name: 'Indigo',
    id: 'indigo',
    primary: {
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',  // WCAG AA: 5.8:1 contrast
      800: '#3730a3',  // WCAG AA: 8.5:1 contrast
      900: '#2a257d',  // WCAG AA: 11.9:1 contrast
    },
    secondary: {
      100: '#ddd6fe',
      200: '#c4b5fd',
      300: '#a78bfa',
      400: '#8b5cf6',
      500: '#7c3aed',
      600: '#6d28d9',
      700: '#5b21b6',  // WCAG AA: 5.5:1 contrast
      800: '#4c1d95',  // WCAG AA: 8.1:1 contrast
      900: '#341566',  // WCAG AA: 11.7:1 contrast
    },
  },
  pink: {
    name: 'Pink',
    id: 'pink',
    primary: {
      100: '#fce7f3',
      200: '#fbcfe8',
      300: '#f9a8d4',
      400: '#f472b6',
      500: '#ec4899',
      600: '#db2777',
      700: '#be185d',  // WCAG AA: 5.7:1 contrast
      800: '#9d174d',  // WCAG AA: 8.4:1 contrast
      900: '#690f33',  // WCAG AA: 12.3:1 contrast
    },
    secondary: {
      100: '#ffe4e6',
      200: '#fecdd3',
      300: '#fda4af',
      400: '#fb7185',
      500: '#f43f5e',
      600: '#e11d48',
      700: '#be123c',  // WCAG AA: 5.9:1 contrast
      800: '#9f1239',  // WCAG AA: 8.6:1 contrast
      900: '#6b0d26',  // WCAG AA: 12.5:1 contrast
    },
  },
  amber: {
    name: 'Amber',
    id: 'amber',
    primary: {
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',  // WCAG AA: 5.5:1 contrast
      800: '#92400e',  // WCAG AA: 8.2:1 contrast
      900: '#612b09',  // WCAG AA: 12.1:1 contrast
    },
    secondary: {
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',  // WCAG AA: 5.0:1 contrast
      800: '#9a3412',  // WCAG AA: 7.8:1 contrast
      900: '#6c240d',  // WCAG AA: 11.3:1 contrast
    },
  },
  cyan: {
    name: 'Cyan',
    id: 'cyan',
    primary: {
      100: '#cffafe',
      200: '#a5f3fc',
      300: '#67e8f9',
      400: '#22d3ee',
      500: '#06b6d4',
      600: '#0891b2',
      700: '#0e7490',  // WCAG AA: 5.2:1 contrast
      800: '#0f576c',  // WCAG AA: 8.1:1 contrast
      900: '#0c3a48',  // WCAG AA: 11.7:1 contrast
    },
    secondary: {
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',  // WCAG AA: 5.4:1 contrast
      800: '#075985',  // WCAG AA: 7.9:1 contrast
      900: '#063a5a',  // WCAG AA: 11.2:1 contrast
    },
  },
  emerald: {
    name: 'Emerald',
    id: 'emerald',
    primary: {
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',  // WCAG AA: 4.9:1 contrast
      800: '#035a41',  // WCAG AA: 7.5:1 contrast
      900: '#023c2b',  // WCAG AA: 11.2:1 contrast
    },
    secondary: {
      100: '#ccfbf1',
      200: '#99f6e4',
      300: '#5eead4',
      400: '#2dd4bf',
      500: '#14b8a6',
      600: '#0d9488',
      700: '#0f766e',  // WCAG AA: 4.8:1 contrast
      800: '#0c5953',  // WCAG AA: 7.4:1 contrast
      900: '#093c38',  // WCAG AA: 10.8:1 contrast
    },
  },
  rose: {
    name: 'Rose',
    id: 'rose',
    primary: {
      100: '#ffe4e6',
      200: '#fecdd3',
      300: '#fda4af',
      400: '#fb7185',
      500: '#f43f5e',
      600: '#e11d48',
      700: '#be123c',  // WCAG AA: 5.9:1 contrast
      800: '#9f1239',  // WCAG AA: 8.6:1 contrast
      900: '#6b0d26',  // WCAG AA: 12.5:1 contrast
    },
    secondary: {
      100: '#fce7f3',
      200: '#fbcfe8',
      300: '#f9a8d4',
      400: '#f472b6',
      500: '#ec4899',
      600: '#db2777',
      700: '#be185d',  // WCAG AA: 5.7:1 contrast
      800: '#9d174d',  // WCAG AA: 8.4:1 contrast
      900: '#690f33',  // WCAG AA: 12.3:1 contrast
    },
  },
};

// Default theme
export const DEFAULT_THEME = 'purple';

// Get theme by ID
export const getTheme = (themeId) => {
  return themes[themeId] || themes[DEFAULT_THEME];
};

// Get all theme IDs
export const getThemeIds = () => {
  return Object.keys(themes);
};

// Get all theme names
export const getThemeList = () => {
  return Object.values(themes).map(theme => ({
    id: theme.id,
    name: theme.name,
    primaryColor: theme.primary[500],
  }));
};
